import { includes, some, map, subtract, sum, get, round, filter, isEmpty, every, find, omit, merge } from 'lodash'

const filteredClusterData = ({ clusters, filters }) => filter(clusters, (cluster) => {
  const filterResults = {}

  if (filters.function != 'All') {
    filterResults['function'] = filters.function === get(cluster, 'function')
  }

  if (filters.ownership != 'All') {
    filterResults['ownership'] = filters.ownership === get(cluster, 'ownership')
  }

  if (filters.locations != 'All') {
    filterResults['locations'] = includes(map(filters.locations, 'value'), get(cluster, 'location_id'))
  }

  if (filters.customers != 'All') {
    filterResults['customers'] = some(map(cluster.vms, 'viaje_client_id'), customerId => includes(map(filters.customers, 'value'), customerId))
  }

  if (filters.customerStatus != 'All') {
    filterResults['customerStatus'] = some(map(cluster.vms, 'customer_status'), status => includes(map(filters.customerStatus, 'value'), status))
  }

  return (every(filterResults, (value, _key) => value === true) || isEmpty(filterResults))
})

const filteredVMs = (vms = [], filters) => filter(vms, (vm) => {
  const filterResults = {}

  if (filters.powerState != 'All') {
    filterResults['powerState'] = vm.power_state.replace('Powered', '') === filters.powerState
  }

  if (filters.customers != 'All') {
    filterResults['customers'] = includes(map(filters.customers, 'value'), vm.viaje_client_id)
  }

  if (filters.customerStatus != 'All') {
    filterResults['customerStatus'] = includes(map(filters.customerStatus, 'value'), vm.customer_status)
  }

  return (every(filterResults, (value, _key) => value === true) || isEmpty(filterResults))
})

const sumVMData = (vms, filters, field) => round(
  sum(map(
    filteredVMs(vms, filters),
    vm => parseFloat(vm[field])
  ))
)

const nPlus1 = (field, host_count, ratio) => round(
  (parseFloat(field) / host_count * (host_count - 1) * ratio)
)

const availableCPU = ({ cluster, filters }) => {
  const cpuCores = filters.nPlus ? nPlus1(cluster.host_cpu_cores, cluster.host_count, filters.ratio) : (cluster.host_cpu_cores * filters.ratio)

  return round(cpuCores - sumVMData(cluster.vms, filters, 'num_cpu'), 2)
}

const availableMemory = ({ cluster, filters }) => {
  const memoryGB = filters.nPlus ? nPlus1(cluster.host_memory_total_gb, cluster.host_count, 1) : cluster.host_memory_total_gb

  return round(memoryGB - sumVMData(cluster.vms, filters, 'memory_gb'), 2)
}

const filteredTrendsData = ({ filteredClusters, filters, trends }) => {
  filteredClusters = filter(filteredClusters, (cluster) => filters.powerState === 'All' ? true : filteredVMs(cluster.vms, filters).length > 0)

  const filteredTrends = filter(trends, trend => {
    return some(filteredClusters, cluster => cluster.cluster_id === trend.id);
  });

  return map(filteredTrends, trend => {
    const matchingCluster = find(filteredClusters, cluster => cluster.cluster_id === trend.id);
    
    return {
      name: trend.name,
      data: map(trend.data, d => {
        const trendWithoutUnwantedKeys = omit(d, ['date']);
        const cluster_trend = merge({}, matchingCluster, trendWithoutUnwantedKeys);

        const clusterCPUCores = filters.nPlus ? nPlus1(cluster_trend.host_cpu_cores, cluster_trend.host_count, filters.ratio) : (cluster_trend.host_cpu_cores * filters.ratio)
        const clusterMemoryGB = filters.nPlus ? nPlus1(cluster_trend.host_memory_total_gb, cluster_trend.host_count, 1) : cluster_trend.host_memory_total_gb
        
        return {
          date: d.date,
          // Total Capacity
          host_count: cluster_trend.host_count,
          host_cpu_cores: filters.nPlus ? nPlus1(cluster_trend.host_cpu_cores, cluster_trend.host_count, filters.ratio) : cluster_trend.host_cpu_cores * filters.ratio,
          host_memory_total_gb: filters.nPlus ? nPlus1(cluster_trend.host_memory_total_gb, cluster_trend.host_count, 1) : cluster_trend.host_memory_total_gb,
          // Usage
          vm_count: filteredVMs(cluster_trend.vms, filters).length,
          num_cpu: sumVMData(cluster_trend.vms, filters, 'num_cpu'),
          cluster_allocated_memory_gb: sumVMData(cluster_trend.vms, filters, 'memory_gb'),
          average_cpu_percent: round(round(parseFloat(cluster_trend.host_cpu_usage_mhz) / cluster_trend.host_cpu_total_mhz, 2) * 100),
          average_memory_percent: round(round(parseFloat(cluster_trend.host_memory_usage_gb) / cluster_trend.host_memory_total_gb, 2) * 100),
          // Available 
          available_cpu_n1: availableCPU({ cluster: cluster_trend, filters }),
          n1_available_cluster_memory_gb: availableMemory({ cluster: cluster_trend, filters }),
          available_cpu_percent: round((parseFloat(availableCPU({ cluster: cluster_trend, filters })) / clusterCPUCores) * 100, 2),
          available_memory_percent: round((parseFloat(availableMemory({ cluster: cluster_trend, filters })) / clusterMemoryGB) * 100, 2)
        }
      })
    }
  });
}

export { filteredClusterData, filteredVMs, sumVMData, nPlus1, availableCPU, availableMemory, filteredTrendsData }
